import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import ApprovedBadge from '@/components/ApprovedBadge';
import NotificationBadge from '@/components/NotificationBadge';
import PostcodeNudgeCard from '@/components/PostcodeNudgeCard';

/**
 * Shared hub layout used by ProBoundHub / ProEpoxyHub / ProDecorHub.
 *
 * Renders the page header (logo + tagline) and three large category panels.
 * Each category contains its own sub-tiles. This keeps the three hub files
 * thin — they only declare a `brand` object and a `categories` array.
 *
 * Props:
 *   brand: {
 *     name: 'ProBound',
 *     fullName: 'Ox Resin ProBound',
 *     tagline: '...',
 *     primaryColor: '#2F5E96',
 *     secondaryColor: '#300122',
 *     logo: 'https://...',
 *     sectionKey: 'probound'  // matches notification keys & ApprovedBadge `section`
 *   }
 *   categories: Array<{
 *     id: string,
 *     title: string,
 *     description: string,
 *     icon: LucideIcon,
 *     tiles: Array<{
 *       id: string,
 *       title: string,
 *       description: string,
 *       icon: LucideIcon,
 *       path: string | null,            // null/undefined = disabled "Coming soon"
 *       notifKey?: 'lead' | 'order'      // optional notification badge category
 *     }>
 *   }>
 *   testIdPrefix: 'probound' | 'proepoxy' | 'prodecor'
 *   notifBySectionCategory: from useNotificationCounts
 */
export default function SectionHubLayout({ brand, categories, testIdPrefix, notifBSC }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbedMode = searchParams.get('embed') === 'true';
  const { user, isAuthenticated } = useAuth();

  const handleNavigate = (path) => {
    if (!path) return;
    navigate(isEmbedMode ? `${path}${path.includes('?') ? '&' : '?'}embed=true` : path);
  };

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      {!isEmbedMode && (
        <header className="bg-white shadow-sm" style={{ borderBottom: `4px solid ${brand.primaryColor}` }}>
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-2 text-gray-600 hover:bg-gray-100"
              data-testid={`${testIdPrefix}-home-btn`}
            >
              <Home className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">All Services</span>
            </Button>
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              {isAuthenticated && user?.account_code && (
                <span
                  className="sm:hidden inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#006400] bg-[#006400]/10 border border-[#006400]/20 rounded-full px-2 py-1"
                  title="Your unique account reference"
                  data-testid={`${testIdPrefix}-header-account-code-mobile`}
                >
                  <span className="opacity-70">Ref</span>
                  <span className="font-mono">{user.account_code}</span>
                </span>
              )}
              {isAuthenticated && (
                <div className="hidden sm:flex flex-col items-end leading-tight">
                  <span className="text-sm text-gray-700 font-medium truncate max-w-[180px]">{user?.name}</span>
                  {user?.account_code && (
                    <span
                      className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#006400] bg-[#006400]/10 border border-[#006400]/20 rounded-full px-2 py-0.5"
                      title="Your unique account reference"
                      data-testid={`${testIdPrefix}-header-account-code`}
                    >
                      <span className="opacity-70">Ref</span>
                      <span className="font-mono">{user.account_code}</span>
                    </span>
                  )}
                </div>
              )}
              {isAuthenticated && brand.sectionKey === 'probound' && (
                <ApprovedBadge user={user} section="probound" />
              )}
              <img
                src={brand.logo}
                alt={brand.fullName}
                className="h-9 sm:h-12 md:h-14 cursor-pointer object-contain bg-white"
                style={{ border: `1.5px solid ${brand.primaryColor}`, borderRadius: '0.5rem', padding: '2px 6px' }}
                onClick={() => navigate(`/${brand.sectionKey}`)}
              />
            </div>
          </div>
        </header>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="text-center mb-8 md:mb-10">
          <div className="flex justify-center mb-4">
            <img
              src={brand.logo}
              alt={brand.fullName}
              className="h-16 md:h-20 object-contain bg-white"
              style={{ border: `2px solid ${brand.primaryColor}`, borderRadius: '0.75rem', padding: '4px 10px' }}
            />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#27292E] mb-2">{brand.fullName}</h1>
          <p className="text-gray-600">{brand.tagline}</p>
        </div>

        {!isEmbedMode && <PostcodeNudgeCard />}

        {/* Three category panels */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {categories.map((cat) => {
            const CatIcon = cat.icon;
            return (
              <section
                key={cat.id}
                data-testid={`${testIdPrefix}-cat-${cat.id}`}
                className="rounded-2xl overflow-hidden shadow-sm border border-gray-200 bg-white flex flex-col"
              >
                {/* Category header */}
                <div
                  className="px-5 py-4 text-white"
                  style={{ background: `linear-gradient(135deg, ${brand.primaryColor}, ${brand.secondaryColor})` }}
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-white/15 rounded-lg p-2">
                      <CatIcon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-base font-bold leading-tight">{cat.title}</h2>
                      <p className="text-white/70 text-xs">{cat.description}</p>
                    </div>
                  </div>
                </div>

                {/* Tiles in this category */}
                <div className="p-3 flex-1 flex flex-col gap-2">
                  {cat.tiles.map((tile) => {
                    const Icon = tile.icon;
                    const disabled = !tile.path;
                    const badgeCount = tile.notifKey && notifBSC?.[brand.sectionKey]?.[tile.notifKey];
                    return (
                      <button
                        key={tile.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => handleNavigate(tile.path)}
                        data-testid={`${testIdPrefix}-tile-${tile.id}`}
                        className={`relative w-full text-left rounded-xl p-3 transition-all flex items-center gap-3 ${
                          disabled
                            ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-50 hover:bg-gray-100 hover:shadow-sm text-gray-900 cursor-pointer'
                        }`}
                      >
                        {badgeCount > 0 && (
                          <NotificationBadge count={badgeCount} position="top-left" size="md" />
                        )}
                        <div
                          className="rounded-lg p-2 shrink-0"
                          style={{
                            background: disabled ? '#e5e7eb' : `${brand.primaryColor}15`,
                            color: disabled ? '#9ca3af' : brand.primaryColor,
                          }}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-bold truncate">{tile.title}</h3>
                            {disabled && (
                              <span className="text-[10px] uppercase tracking-wider bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">
                                Coming Soon
                              </span>
                            )}
                          </div>
                          <p className={`text-xs ${disabled ? 'text-gray-400' : 'text-gray-500'} truncate`}>
                            {tile.description}
                          </p>
                        </div>
                        {!disabled && <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </main>
    </div>
  );
}
